import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  AddCardBox,
  Box,
  Button,
  CardBox,
  CircularProgress,
  DialogModal,
  MenuItem,
  ModalBox,
  PageHeader,
  SearchIcon,
  TextField,
} from 'components';
import { useIsMobile } from 'hooks';
import {
  Add,
  Briefcase,
  CloseCircle,
  Edit2,
  Filter,
  FilterRemove,
  Trash,
} from 'iconsax-react';

import { OrganizationCreateEditForm } from './OrganizationCreateEditForm';

import {
  ContentLoader,
  _,
  styled,
  useNavigate,
  useSelector,
  useTranslation,
} from 'third-party';

import { USER_INPUT_DEBOUNCE_TIME_MS } from 'constants/common';
import { userOrganizationPaymentListUrl } from 'constants/url';

import { useApp } from 'modules/app';
import { useOrganization } from 'modules/organization';

import { appSelectors, organizationSelectors } from 'selectors';

import { getOrganizationAvatarUrl, hasRights } from 'utils/app';

import { APIOrganization, APIUserRight, APIUserType } from 'types/api';

import { palette } from 'styles/theme';

const StyleInput = styled(TextField)`
  .MuiInput-input {
    padding: 0.4rem !important;
    min-width: 18rem;
  }
`;

export const OrganizationList = () => {
  const { t } = useTranslation();
  const [isFilteringOn, setIsFilteringOn] = useState(false);
  const [selectedItem, setSelectedItem] = useState<APIOrganization | null>(
    null,
  );

  const { userInfo, selectOrganization } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isMobile = useIsMobile();
  const ref = useRef<HTMLInputElement>(null);
  const items = useSelector(organizationSelectors.organizationList);
  const allOrganizationLoaded = useSelector(
    organizationSelectors.allOrganizationsLoaded,
  );
  const organizationFilters = useSelector(
    organizationSelectors.organizationFilters,
  );
  const loadOrganizationsPending = useSelector(
    organizationSelectors.loadMoreOrganizationsPending,
  );
  const loadMoreOrganizationPending = useSelector(
    organizationSelectors.loadMoreOrganizationsPending,
  );
  const organizationsListLoaded = useSelector(
    organizationSelectors.organizationsListLoaded,
  );

  const query = organizationFilters.query || '';

  const {
    deleteOrganization,
    loadOrganizations,
    loadMoreOrganizations,
    updateOrganizationFilters,
    clearOrganizationFilters,
    resetOrganizationFilters,
  } = useOrganization();

  useEffect(() => {
    loadOrganizations();
  }, [organizationFilters, loadOrganizations]);

  const organizations = useSelector(appSelectors.organizations);
  const isSuperAdmin = useSelector(appSelectors.isSuperAdmin);
  const navigate = useNavigate();

  const isExternalOrganization = useCallback(
    (item: APIOrganization) =>
      isSuperAdmin &&
      // Real memberships only — synthetic external-org entries are tagged SuperAdmin.
      !organizations?.some(
        it =>
          it.organization.id == item.id &&
          it.userType !== APIUserType.SuperAdmin,
      ),
    [isSuperAdmin, organizations],
  );

  const onCardClick = useCallback(
    (item: APIOrganization) => {
      selectOrganization(item.id, item);
      navigate(userOrganizationPaymentListUrl(item.id));
    },
    [navigate, selectOrganization],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearchInputChange = useCallback(
    _.debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        updateOrganizationFilters({
          ...organizationFilters,
          query: event.target.value,
        });
      }
    }, USER_INPUT_DEBOUNCE_TIME_MS),
    [updateOrganizationFilters, organizationFilters],
  );

  useEffect(() => {
    return () => {
      clearOrganizationFilters();
    };
  }, [clearOrganizationFilters]);

  const cardMenuItems = (item: APIOrganization, handleClose: () => void) => {
    const hasAccess = hasRights(
      organizations,
      APIUserRight.ManageOrganization,
      item.id,
    );

    if (!hasAccess) {
      return undefined;
    }

    return [
      <MenuItem
        key="Edit"
        onClick={() => {
          setSelectedItem(item);
          setIsModalOpen(true);
          handleClose();
        }}
      >
        <Edit2 variant="TwoTone" size="24" />
        {t('organizationList.menuItem.edit')}
      </MenuItem>,
      <MenuItem
        key="Remove"
        onClick={() => {
          setSelectedItem(item);
          handleClose();
        }}
      >
        <Trash variant="TwoTone" size="24" />
        {t('organizationList.menuItem.remove')}
      </MenuItem>,
    ];
  };

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      const bottom =
        Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
        1;

      if (bottom && !loadMoreOrganizationPending && !allOrganizationLoaded) {
        loadMoreOrganizations();
      }
    },
    [allOrganizationLoaded, loadMoreOrganizationPending, loadMoreOrganizations],
  );

  const onFilterReset = useCallback(() => {
    resetOrganizationFilters();
    if (ref.current) {
      ref.current.value = '';
    }
  }, [resetOrganizationFilters]);

  const getSubTitle = useCallback(
    (item: APIOrganization) => {
      const userOrganization = organizations?.find(
        it => it.organization.id == item.id,
      );
      if (!userOrganization) {
        return t('organizationList.userRole.notInTheOrganization');
      }

      return userOrganization.userType == APIUserType.Admin
        ? t('organizationList.userRole.admin')
        : t('organizationList.userRole.user');
    },
    [organizations, t],
  );

  const onAddOrganizationClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    userInfo();
    setIsModalOpen(false);
    setSelectedItem(null);
  }, [userInfo]);

  return (
    <Box width="100%" overflow="auto" onScroll={handleScroll}>
      <Box
        position="sticky"
        top={0}
        zIndex={11}
        bgcolor="white"
        alignItems="center"
      >
        <PageHeader
          header={t('organizationList.header')}
          subHeader={t('organizationList.subHeader')}
          icon={
            <Briefcase variant="TwoTone" color={palette.primary900} size="28" />
          }
        >
          <>
            {isFilteringOn && (
              <Button variant="text" onClick={onFilterReset}>
                <CloseCircle variant="TwoTone" size="24" />
              </Button>
            )}
            <Button variant="text" onClick={onAddOrganizationClick}>
              <Add variant="TwoTone" size="24" />
            </Button>
            <Button variant="text" onClick={() => setIsFilteringOn(it => !it)}>
              {isFilteringOn ? (
                <Filter variant="TwoTone" size="24" />
              ) : (
                <FilterRemove variant="TwoTone" size="24" />
              )}
            </Button>
          </>
        </PageHeader>

        {isFilteringOn && (
          <Box
            display="flex"
            alignItems="center"
            gap="0.5rem"
            padding="1rem 1rem 0 0 "
          >
            <SearchIcon color={palette.primary900} size="20" />
            <StyleInput
              inputRef={ref}
              autoComplete="off"
              autoCorrect="off"
              id="input-with-sx"
              onChange={onSearchInputChange}
              variant="standard"
            />
          </Box>
        )}
      </Box>
      {loadOrganizationsPending && (
        <Box
          display="grid"
          gap="1.25rem"
          padding="1rem 0 0.5rem 0"
          sx={{
            gridTemplateColumns:
              'repeat(auto-fill, minmax(min(100%, 17rem), 1fr))',
          }}
        >
          {Array.from({ length: 8 }, (_, index) => (
            <Box
              key={`skeleton-${index}`}
              width="100%"
              height="11rem"
              sx={{
                borderRadius: '12px',
                boxShadow:
                  '0 2px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.03)',
                overflow: 'hidden',
              }}
            >
              <ContentLoader
                speed={2}
                width={272}
                height={176}
                viewBox="0 0 272 176"
                backgroundColor="#f3f3f3"
                foregroundColor="#ecebeb"
              >
                {/* Avatar */}
                <circle cx="37" cy="38" r="21" />
                {/* Title */}
                <rect x="70" y="26" rx="4" ry="4" width="118" height="13" />
                {/* Subtitle */}
                <rect x="70" y="46" rx="4" ry="4" width="78" height="10" />
                {/* Menu icon */}
                <rect x="232" y="22" rx="4" ry="4" width="22" height="22" />
                {/* Divider */}
                <rect x="16" y="74" rx="0" ry="0" width="240" height="1" />
                {/* Description lines */}
                <rect x="16" y="86" rx="4" ry="4" width="224" height="10" />
                <rect x="16" y="102" rx="4" ry="4" width="200" height="10" />
                <rect x="16" y="118" rx="4" ry="4" width="176" height="10" />
              </ContentLoader>
            </Box>
          ))}
        </Box>
      )}
      {!loadOrganizationsPending && (
        <Box
          display="grid"
          gap="1.25rem"
          padding="1rem 0 0.5rem 0"
          sx={{
            gridTemplateColumns:
              'repeat(auto-fill, minmax(min(100%, 17rem), 1fr))',
          }}
        >
          {items?.map(it => {
            const isExternal = isExternalOrganization(it);

            return (
              <CardBox<APIOrganization>
                item={it}
                key={it.id}
                title={it.name}
                stripeText={
                  it.isSandbox ? t('organizationList.sandboxLabel') : undefined
                }
                badgeText={
                  isExternal ? t('organizationList.externalLabel') : undefined
                }
                alertText={
                  it.isDisabled
                    ? t('organizationList.disabledLabel')
                    : undefined
                }
                greyedOut={isExternal}
                avatarUrl={getOrganizationAvatarUrl(it)}
                width="100%"
                height="11rem"
                subTitle={getSubTitle(it)}
                description={it.description}
                descriptionMaxLines={3}
                onClick={onCardClick}
                menuItems={cardMenuItems}
                highlightQuery={query}
              />
            );
          })}
          {organizationsListLoaded && (
            <AddCardBox
              minHeight="11rem"
              label={t('organizationList.addTile.label')}
              onClick={onAddOrganizationClick}
            />
          )}
        </Box>
      )}
      {loadMoreOrganizationPending && (
        <Box display="flex" justifyContent="space-around" padding="1rem">
          <CircularProgress />
        </Box>
      )}
      {isModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '40rem'}
          height="35rem"
          onClose={onModalClose}
        >
          <OrganizationCreateEditForm
            onModalClose={onModalClose}
            organizationId={selectedItem?.id}
          />
        </ModalBox>
      )}
      <DialogModal
        onClose={() => setSelectedItem(null)}
        open={!!selectedItem && !isModalOpen}
        description={t('organizationList.deleteConfirm')}
        handleActionClick={() => {
          if (selectedItem) {
            deleteOrganization(selectedItem.id);
          }
          setSelectedItem(null);
        }}
      />
    </Box>
  );
};
